import { GoogleMap, Marker } from '@react-google-maps/api';
import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';

import { ReactComponent as ClockIcon } from '../../assets/icons/clock-icon-v2.svg';
import { ReactComponent as CloseIcon } from '../../assets/icons/close-circle.svg';
import { ReactComponent as ConnectIcon } from '../../assets/icons/devices-connect-icon.svg';
import { ReactComponent as EnergyIcon } from '../../assets/icons/energy-icon.svg';
// import { ReactComponent as NewTabIcon } from '../../assets/icons/tables/new-tab.svg';
import { useGoogleMaps } from '../../contexts/google-map-context/GoogleMapContent';
import { useAppDispatch } from '../../redux/hooks';
import { setNotificationMessage } from '../../redux/slices/NotificationManagementSlice';
import ToFixedValue from '../../shared-functions/ToFixedValue';
import emspCdrApi from '../../utils/api/v2/emsp-cdr';

import ChargingSpeedSocChart from './component/ChargingSpeedSocChart';
import MeterHardwareStats from './component/MeterHardwareStats';
import DeviceSidePanel from './DeviceSidePanel';

/**
 * Get exact duration between two ISO timestamps or from durationSeconds in HHh MMm format
 * @param {string} [startIso] - The start ISO timestamp
 * @param {string} [endIso] - The end ISO timestamp
 * @param {number} [durationSeconds] - Duration in seconds
 * @returns {string} Formatted duration string
 */
const getExactDuration = (startIso?: string, endIso?: string, durationSeconds?: number): string => {
  let totalSeconds = 0;

  if (durationSeconds !== undefined && durationSeconds !== null && durationSeconds > 0) {
    totalSeconds = Math.floor(durationSeconds);
  } else {
    if (!startIso || !endIso) return '--';

    const start = new Date(startIso).getTime();
    const end = new Date(endIso).getTime();

    const diffMs = Math.max(end - start, 0);
    totalSeconds = Math.floor(diffMs / 1000);
  }

  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  return `${String(hours).padStart(2, '0')}hrs ${String(minutes).padStart(
    2,
    '0',
  )}m ${String(seconds).padStart(2, '0')}s`;
};

/**
 * Generates an SVG station marker icon encoded as a data URI.
 * @param {string} color - The hex color code for the marker.
 * @returns {string} The data URI of the generated SVG icon.
 */
const getStationMarkerIcon = (color: string): string => {
  const svg = `
    <svg width="44" height="45" viewBox="0 0 44 45"
      fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M22 2.91602C14.4186 2.91602 8.25 8.81563 8.25 16.0645C8.25 19.5166 9.82352 24.1073 12.9267 29.7096C15.4189 34.2076 18.3021 38.275 19.8017 40.2988C20.0551 40.6446 20.3864 40.9259 20.7688 41.1197C21.1512 41.3136 21.5739 41.4146 22.0026 41.4146C22.4313 41.4146 22.854 41.3136 23.2363 41.1197C23.6187 40.9259 23.95 40.6446 24.2034 40.2988C25.7005 38.275 28.5863 34.2076 31.0784 29.7096C34.1765 24.1091 35.75 19.5183 35.75 16.0645C35.75 8.81563 29.5814 2.91602 22 2.91602Z"
        fill="${color}"
      />
      <path
        fill-rule="evenodd"
        clip-rule="evenodd"
        d="M23.2551 19.1954H28.9444C29.3221 19.1944 29.6668 19.386 29.8276 19.6883C29.9884 19.9907 29.9367 20.3475 29.6944 20.6041L19.3279 31.6541C19.1341 31.8446 18.8213 31.9 18.5603 31.7892C18.2993 31.6784 18.1517 31.4285 18.2002 31.1757L20.3058 21.7309H15.0504C14.7553 21.7289 14.4766 21.6094 14.2927 21.4052C14.1088 21.202 14.0394 20.9356 14.1033 20.6809L16.9226 9.63769C17.0184 9.24687 17.4083 8.96785 17.8609 8.96591H25.2198C25.5259 8.96202 25.8145 9.08938 25.9951 9.30715C26.1823 9.51812 26.2428 9.79616 26.1591 10.0538L23.2551 19.1954Z"
        fill="white"
      />
    </svg>
  `;
  return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`;
};

/**
 * Resolves the color of the station marker based on connector/session status.
 * @param {any} sessionData - The session data object
 * @returns {string} The hex color code
 */
const getSessionMarkerColor = (sessionData: any): string => {
  const statuses = sessionData?.connectorStatuses || sessionData?.station?.connectorStatuses || [];
  if (statuses.length > 0) {
    const hasAvailable = statuses.some((item: any) => item.status === 'Available');
    if (hasAvailable) return '#22c55e';

    const hasCharging = statuses.some(
      (item: any) =>
        item.status === 'Charging' || item.status === 'Preparing' || item.status === 'Finishing',
    );
    if (hasCharging) return '#3b82f6';

    const hasReserved = statuses.some((item: any) => item.status === 'Reserved');
    if (hasReserved) return '#FFC107';

    return '#e41010';
  }

  const sessionStatus = sessionData?.status;
  if (sessionStatus === 'Available') return '#22c55e';
  if (
    sessionStatus === 'inProgress' ||
    sessionStatus === 'Charging' ||
    sessionStatus === 'Preparing'
  )
    return '#3b82f6';
  if (sessionStatus === 'Reserved') return '#FFC107';
  if (sessionStatus === 'completed' || sessionStatus === 'Finished') return '#22c55e';

  return '#3b82f6';
};

/** Props for ChargingSessionSidePanel */
interface ChargingSessionSidePanelProperties {
  fetchIdFrom: 'queryParams' | 'props';
  id?: string;
  datatype?: string;
}

/** Live session calculated values */
interface LiveInfo {
  totalkWhConsumed: number;
  paidAmount: number;
}

/** SOC vs kW chart data point */
interface SocKwPoint {
  ts: string;
  kw: number;
  soc: number;
}

/** Vehicle information */
interface VehicleInfo {
  manufacturer: string;
  model: string;
  connectorType: string;
  makeYear?: string;
  fullChargeRange?: number | null;
}

/** Wallet history */
interface WalletHistory {
  amount: string;
  paymentStatus: string;
}

/** Cost breakdown information */
interface CostInfo {
  total: number;
  consumptionCost: number;
  platformFee: number;
  sessionFee: number;
  idlePenalty: number | null;
  currency: string;
}

/** Charging session API response */
interface ChargingSessionResponse {
  desiredChargePercentage: number;
  chargingStation: string;
  powerConsumedInKwh: string;
  transactionStartsAt: string;
  transactionEndsAt?: string;
  durationSeconds?: number;
  finalBatteryCapacity?: number;
  vehicle?: VehicleInfo;
  chargingStationName: string;
  locationName?: string;
  chargingPointName: string;
  dynamicPricing?: { price: string };
  price?: string;
  connector?: number;
  chargingPointSn?: string;
  status?: string;
  startMethod?: string;
  socVsKW?: SocKwPoint[];
  walletHistoryObject?: WalletHistory;
  estAmount?: string;
  latitude: string;
  longitude: string;
  costDetails?: CostInfo;
  paymentMethod?: string;
  paymentStatus?: string;
  paymentFailedReason?: string;
  isPaid?: boolean;
  isInvoiceGenerated?: boolean;
  invoiceNumber?: string | null;
}

/**
 * Convert lat/lng strings to valid Google map position
 * @param {string} [lat] - Latitude string
 * @param {string} [lng] - Longitude string
 * @returns {any} Google maps LatLngLiteral or null
 */
const getValidPosition = (lat?: string, lng?: string): google.maps.LatLngLiteral | null => {
  const latitude = Number(lat);
  const longitude = Number(lng);

  if (Number.isNaN(latitude) || Number.isNaN(longitude)) return null;

  return { lat: latitude, lng: longitude };
};

/**
 * Resolves the display name for a vehicle.
 * @param {VehicleInfo} [vehicle] - The vehicle information object.
 * @returns {string} The formatted vehicle name or 'N/A' if not available.
 */
const getVehicleName = (vehicle?: VehicleInfo): string => {
  if (!vehicle) return 'N/A';

  const manufacturer =
    vehicle.manufacturer && vehicle.manufacturer !== 'null' ? vehicle.manufacturer.trim() : '';
  const model = vehicle.model && vehicle.model !== 'null' ? vehicle.model.trim() : '';
  const fullName = `${manufacturer} ${model}`.trim();

  return fullName || 'N/A';
};

/**
 * ChargingSessionSidePanel
 * Displays detailed information about a charging session
 * @param {ChargingSessionSidePanelProperties} properties - The properties
 * @returns {any} JSX Element rendering session details
 */
function ChargingSessionSidePanel(properties: ChargingSessionSidePanelProperties): JSX.Element {
  const { t } = useTranslation('endConsumer');
  const { isLoaded } = useGoogleMaps();
  const dispatch = useAppDispatch();
  const { fetchIdFrom, id, datatype } = properties;
  const [searchParameters] = useSearchParams();

  const [sessionData, setSessionData] = useState<ChargingSessionResponse | null>(null);
  const [liveInfo, setLiveInfo] = useState<LiveInfo>({
    totalkWhConsumed: 0,
    paidAmount: 0,
  });
  const [overlayPanel, setOverlayPanel] = useState<'closed' | 'station' | 'device'>('closed');

  const mapCenter = getValidPosition(sessionData?.latitude, sessionData?.longitude);

  /**
   * Map container style object for GoogleMap component
   */
  const mapContainerStyle = useMemo(() => ({ width: '100%', height: '100%' }), []);

  /**
   * Calculates session duration and financials
   * @param {ChargingSessionResponse} data - The session response data
   * @returns {void}
   */
  const computeLiveInfo = useCallback((data: ChargingSessionResponse): void => {
    const energy = Number.parseFloat(data.powerConsumedInKwh || '0');
    const amount = Number.parseFloat(data.walletHistoryObject?.amount || data.estAmount || '0');

    setLiveInfo({
      totalkWhConsumed: energy,
      paidAmount: amount,
    });
  }, []);

  // /**
  //  * Opens overlay side panels
  //  * @param {'station' | 'device'} type - The type of overlay panel to open
  //  * @returns {void}
  //  */
  // const handleOpenOverlayPanel = useCallback((type: 'station' | 'device'): void => {
  //   setOverlayPanel(type);
  // }, []);

  /**
   * Closes overlay side panel
   * @returns {void}
   */
  const handleCloseOverlayPanel = useCallback((): void => {
    setOverlayPanel('closed');
  }, []);

  /**
   * Static map options (memoized once)
   */
  const miniMapOptions = useMemo<google.maps.MapOptions>(
    () => ({
      disableDefaultUI: true,
      clickableIcons: false,
      draggable: false,
      zoomControl: false,
      scrollwheel: false,
    }),
    [],
  );

  /**
   * Fetch charging session details from API
   * @param {string} chargingId - The charging session ID
   * @returns {Promise<void>} A promise that resolves when the session details are fetched
   */
  const fetchChargingSession = useCallback(
    async (chargingId: string): Promise<void> => {
      try {
        const { data } = await emspCdrApi.fetchEmspCdrDetailsBySessionId(chargingId);
        const cdr = data?.cdr ?? data;

        // Calculate dynamic charging cost per kWh
        const consumptionCost = cdr?.cost?.consumptionCost ?? 0;
        const energyConsumedKwh = cdr?.energyConsumedKwh ?? 0;
        const pricePerKwh =
          energyConsumedKwh > 0 ? ToFixedValue(consumptionCost / energyConsumedKwh, 2) : '0';

        // Parse connector number from deviceNo if format Sn_# is used (e.g. SN987654321_1)
        let connectorNumber = 1;
        const deviceNo = cdr?.station?.deviceNo ?? '';
        if (deviceNo && deviceNo.includes('_')) {
          const parts = deviceNo.split('_');
          const parsed = Number.parseInt(parts[parts.length - 1], 10);
          if (!Number.isNaN(parsed)) {
            connectorNumber = parsed;
          }
        } else if (cdr?.connector) {
          connectorNumber = cdr.connector;
        }

        const session: ChargingSessionResponse = {
          desiredChargePercentage:
            cdr?.socChargingSpeedGraph && cdr.socChargingSpeedGraph.length > 0
              ? (cdr.socChargingSpeedGraph[cdr.socChargingSpeedGraph.length - 1]?.soc ?? 0)
              : 0,
          chargingStation: cdr?.station?.id ?? cdr?.chargingStation ?? '',
          powerConsumedInKwh: String(cdr?.energyConsumedKwh ?? cdr?.powerConsumedInKwh ?? 0),
          transactionStartsAt: cdr?.startedAt ?? cdr?.transactionStartsAt ?? '',
          transactionEndsAt: cdr?.endedAt ?? cdr?.transactionEndsAt ?? '',
          durationSeconds: cdr?.durationSeconds,
          vehicle: cdr?.vehicle
            ? {
                manufacturer:
                  (cdr.vehicle.brand && cdr.vehicle.brand !== 'null' ? cdr.vehicle.brand : '') ||
                  (cdr.vehicle.manufacturer && cdr.vehicle.manufacturer !== 'null'
                    ? cdr.vehicle.manufacturer
                    : ''),
                model: cdr.vehicle.model && cdr.vehicle.model !== 'null' ? cdr.vehicle.model : '',
                connectorType:
                  cdr.vehicle.connectorType || cdr?.station?.connectorType || '',
                makeYear: cdr.vehicle.makeYear ?? '',
                fullChargeRange: cdr.vehicle.fullChargeRange ?? null,
              }
            : undefined,
          chargingStationName: cdr?.station?.name ?? cdr?.chargingStationName ?? '--',
          locationName: cdr?.station?.location ?? cdr?.locationName ?? cdr?.location ?? '',
          chargingPointName: deviceNo || cdr?.chargingPointName || '--',
          price: pricePerKwh,
          connector: connectorNumber,
          chargingPointSn: deviceNo || cdr?.chargingPointSn || '--',
          status: cdr?.status ?? 'unknown',
          startMethod: cdr?.startMethod,
          socVsKW: cdr?.socChargingSpeedGraph ?? [],
          walletHistoryObject: {
            amount: String(cdr?.cost?.total ?? cdr?.estAmount ?? 0),
            paymentStatus: cdr?.paymentStatus ?? 'unknown',
          },
          estAmount: String(cdr?.cost?.total ?? cdr?.estAmount ?? 0),
          latitude:
            cdr?.station?.latitude ??
            cdr?.station?.coordinates?.latitude ??
            cdr?.latitude ??
            cdr?.coordinates?.latitude ??
            '0',
          longitude:
            cdr?.station?.longitude ??
            cdr?.station?.coordinates?.longitude ??
            cdr?.longitude ??
            cdr?.coordinates?.longitude ??
            '0',
          costDetails: cdr?.cost
            ? {
                total: cdr.cost.total ?? 0,
                consumptionCost: cdr.cost.consumptionCost ?? 0,
                platformFee: cdr.cost.platformFee ?? 0,
                sessionFee: cdr.cost.sessionFee ?? 0,
                idlePenalty: cdr.cost.idlePenalty ?? null,
                currency: cdr.cost.currency ?? 'INR',
              }
            : undefined,
          paymentMethod: cdr?.paymentMethod,
          paymentStatus: cdr?.paymentStatus,
          paymentFailedReason: cdr?.paymentFailedReason,
          isPaid: cdr?.isPaid,
          isInvoiceGenerated: cdr?.isInvoiceGenerated,
          invoiceNumber: cdr?.invoiceNumber,
        };

        setSessionData(session);
        computeLiveInfo(session);
      } catch (error: unknown) {
        dispatch(
          setNotificationMessage({
            status: true,
            message: t('unableToFetchChargingSessionDetails'),
            type: 'error',
            code: 0,
          }),
        );
      }
    },
    [computeLiveInfo, dispatch, t],
  );

  useEffect(() => {
    let stationId;
    if (fetchIdFrom === 'queryParams') {
      stationId = searchParameters.get('chargingId');
    } else if (fetchIdFrom === 'props') {
      stationId = id;
    }

    if (!stationId) {
      return;
    }
    fetchChargingSession(stationId);
  }, [searchParameters, id, fetchIdFrom, fetchChargingSession]);

  /**
   * Renders overlay panel content
   * @returns {any} Overlay panel element or null
   */
  const renderOverlayPanel = (): JSX.Element | null => {
    if (!sessionData) return null;

    if (overlayPanel === 'device') {
      return <DeviceSidePanel fetchIdFrom="props" id={sessionData.chargingPointSn ?? ''} />;
    }

    return null;
  };

  return (
    <div className="charging-session-side-panel">
      <div className="charging-session-side-content">
        <MeterHardwareStats
          value={sessionData?.desiredChargePercentage ?? 0}
          finishedTime={getExactDuration(
            sessionData?.transactionStartsAt,
            sessionData?.transactionEndsAt,
            sessionData?.durationSeconds,
          )}
          energy={`${sessionData?.powerConsumedInKwh ?? 0} kWh`}
          completed={sessionData?.status}
        />
        <div className="vehicle-ideals">
          <p className="heading">{t('vehicleName')}</p>
          <p className="values">{getVehicleName(sessionData?.vehicle)}</p>
        </div>
        <div className="live-info">
          <div className="live-info__item">
            <p className="label">{t('energy')}</p>
            <p className="value">
              <EnergyIcon className="icon" />
              {ToFixedValue(liveInfo.totalkWhConsumed, 3)} kWh
            </p>
          </div>
          {datatype !== 'personal' && (
            <div className="live-info__item">
              <p className="label">{t('cost')}</p>
              <p className="value">
                <span>{ToFixedValue(liveInfo.paidAmount, 2)}</span>
                <span>{sessionData?.costDetails?.currency ?? 'SAR'}</span>
              </p>
            </div>
          )}
          <div className="live-info__item">
            <p className="label">{t('elapsedTime')}</p>
            <p className="value">
              <ClockIcon className="icon" />
              <span>
                {getExactDuration(
                  sessionData?.transactionStartsAt,
                  sessionData?.transactionEndsAt,
                  sessionData?.durationSeconds,
                )}
              </span>
            </p>
          </div>
        </div>
        <ChargingSpeedSocChart data={sessionData?.socVsKW ?? []} />
        <div className="station-devices">
          {datatype !== 'personal' && (
            <>
              <div className="content-list">
                <p className="title">{t('stationName')}</p>
                <p className="value" role="presentation">
                  {sessionData?.chargingStationName ?? '--'}
                </p>
              </div>
              {sessionData?.locationName && (
                <div className="content-list">
                  <p className="title">{t('location', 'Location')}</p>
                  <p className="value">{sessionData.locationName}</p>
                </div>
              )}
            </>
          )}

          <div className="content-list">
            <p className="title">{t('device')}</p>
            <p className="value" role="presentation">
              {sessionData?.chargingPointName ?? '--'}
            </p>
          </div>
          {datatype !== 'personal' && (
            <div className="content-list">
              <p className="title">{t('chargeCostPerKwh')}</p>
              <p className="value">
                {sessionData?.price ?? ToFixedValue(sessionData?.dynamicPricing?.price) ?? 0}
                <span> {sessionData?.costDetails?.currency ?? ' '}</span>
              </p>
            </div>
          )}
          {sessionData?.costDetails && datatype !== 'personal' && (
            <>
              <div className="content-list">
                <p className="title">{t('consumptionCost', 'Consumption Cost')}</p>
                <p className="value">
                  {ToFixedValue(sessionData.costDetails.consumptionCost, 2)}
                  <span> {sessionData.costDetails.currency}</span>
                </p>
              </div>
              {Number(sessionData.costDetails.platformFee) > 0 && (
                <div className="content-list">
                  <p className="title">{t('platformFee', 'Platform Fee')}</p>
                  <p className="value">
                    {ToFixedValue(sessionData.costDetails.platformFee, 2)}
                    <span> {sessionData.costDetails.currency}</span>
                  </p>
                </div>
              )}
              {Number(sessionData.costDetails.sessionFee) > 0 && (
                <div className="content-list">
                  <p className="title">{t('sessionFee', 'Session Fee')}</p>
                  <p className="value">
                    {ToFixedValue(sessionData.costDetails.sessionFee, 2)}
                    <span> {sessionData.costDetails.currency}</span>
                  </p>
                </div>
              )}
            </>
          )}
          {sessionData?.paymentStatus && (
            <div className="content-list">
              <p className="title">{t('paymentStatus', 'Payment Status')}</p>
              <p className="value">
                {sessionData.paymentStatus}
                {sessionData.paymentFailedReason ? ` (${sessionData.paymentFailedReason})` : ''}
              </p>
            </div>
          )}
        </div>

        {datatype !== 'personal' && isLoaded && (
          <div className="mini-map">
            <GoogleMap
              mapContainerStyle={mapContainerStyle}
              center={mapCenter || { lat: 11.0251671, lng: 77.02125625 }}
              zoom={12}
              options={miniMapOptions}>
              <Marker
                position={mapCenter || { lat: 11.0251671, lng: 77.02125625 }}
                icon={{
                  url: getStationMarkerIcon(getSessionMarkerColor(sessionData)),
                  scaledSize: new window.google.maps.Size(36, 36),
                }}
              />
            </GoogleMap>
          </div>
        )}
        <div className="card" data-testid="connector-card">
          <div className="leftSection">
            <div className="imageWrapper">
              <ConnectIcon />
              <span className="connectorType">{sessionData?.vehicle?.connectorType || 'N/A'}</span>
            </div>
            <div className="details">
              <h3 className="name">
                {t('connector')} {sessionData?.connector ?? '--'}
              </h3>
              <span className="connectorId">{sessionData?.chargingPointSn ?? '--'}</span>
            </div>
          </div>
          <div className="rightSection">
            <span
              className={
                sessionData?.status === 'finished' ? 'availableBadge' : 'unavailableBadge'
              }>
              {sessionData?.status ?? t('unknown')}
            </span>
            <span className="power">{sessionData?.powerConsumedInKwh ?? 0} kWh</span>
          </div>
        </div>
      </div>
      <div className="device-side-panel-description" />
      <div
        className={`device-panel-station-view-wrap ${overlayPanel !== 'closed' ? 'active' : ''}`}>
        <div className="side-panel-header">
          <span className="disabled-icon">{/* <NewTabIcon /> */}</span>
          <span role="presentation" onClick={handleCloseOverlayPanel}>
            <CloseIcon />
          </span>
        </div>
        <div className="side-panel-content">{renderOverlayPanel()}</div>
      </div>
    </div>
  );
}

export default ChargingSessionSidePanel;
